import {
  Box,
  Container,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import { useAuth } from "@ssf/auth-components";
import type { MyTournamentGroupResponse } from "@ssf/memdb-api";
import type { TournamentRoom } from "@ssf/play-common";
import { tournamentRoomSchema, z } from "@ssf/play-common";
import {
  orderByChild,
  roomsRef,
  startAt,
  useValue,
} from "@ssf/play-components";
import { format, parseISO } from "date-fns";
import { sv } from "date-fns/locale";
import { ChevronRight, Trophy } from "mdi-material-ui";
import { type PropsWithChildren, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import {
  generatePath,
  getStartTime,
  hasNotEnded,
  sortByStartTime,
  useMemberTournamentGroups,
} from "../lib";

export const MemberTournaments = () => {
  const { t } = useTranslation("MemberTournaments");
  const { memberTournaments } = useMemberTournaments();

  return (
    <MemberTournamentsContainer>
      <List disablePadding>
        {memberTournaments.map(({ roomSlug, room, group }) => (
          <ListItemButton
            key={roomSlug}
            component={Link}
            to={generatePath("lobby", { room: roomSlug })}
            sx={{
              py: 1.5,
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              "&:last-of-type": { borderBottom: "none" },
            }}
          >
            <ListItemText
              primary={room.name || roomSlug}
              slotProps={{
                primary: {
                  fontWeight: 600,
                },
              }}
              secondary={
                group.start
                  ? (getStartTime(group) ?? Infinity) < Date.now()
                    ? t("tournamentStarted", {
                        time: formatStartTime(group),
                      })
                    : t("tournamentStarting", {
                        time: formatStartTime(group),
                      })
                  : undefined
              }
            />
            <ChevronRight color="action" />
          </ListItemButton>
        ))}
      </List>
    </MemberTournamentsContainer>
  );
};

const MemberTournamentsContainer = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation("MemberTournaments");

  return (
    <Container maxWidth="md" sx={{ pt: 4 }}>
      <Paper
        sx={{
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1.5,
            color: (theme) => theme.palette.primary.contrastText,
            background: (theme) => theme.palette.primary.main,
          }}
        >
          <Trophy />
          <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
            {t("title")}
          </Typography>
        </Box>
        {children}
      </Paper>
    </Container>
  );
};

const queryConstraints = [
  orderByChild("updated"),
  startAt("2026-08-01T00:00:00.000Z"),
];

const schema = z.record(tournamentRoomSchema);

export const useMemberTournaments = () => {
  const { user } = useAuth();
  const memberId = user ? Number(user.profile.sub) : undefined;

  const { data: rooms = {} } = useValue(roomsRef)({ queryConstraints, schema });

  const tournamentRooms = useMemo(
    () =>
      Object.entries(rooms).filter(
        (entry): entry is [string, TournamentRoom] =>
          entry[1].type === "tournament",
      ),
    [rooms],
  );

  const {
    data: groups,
    isLoading,
    isError,
    error,
  } = useMemberTournamentGroups(tournamentRooms, memberId);

  const upcomingOrOngoingMemberTournaments = useMemo(() => {
    if (!memberId || !groups) {
      return [];
    }

    const groupMap = new Map(groups.map((group) => [group.id, group]));

    return tournamentRooms
      .filter(([, room]) => groupMap.has(room.memdbGroupId))
      .map(([roomSlug, room]) => ({
        roomSlug,
        room,
        group: groupMap.get(room.memdbGroupId)!,
      }))
      .filter(({ group }) => hasNotEnded(group))
      .sort((tournamentA, tournamentB) =>
        sortByStartTime(tournamentA.group, tournamentB.group),
      );
  }, [tournamentRooms, groups, memberId]);

  return {
    memberTournaments: upcomingOrOngoingMemberTournaments,
    isLoading,
    isError,
    error,
  };
};

export type MemberTournament = ReturnType<
  typeof useMemberTournaments
>["memberTournaments"][number];

const formatStartTime = (group: MyTournamentGroupResponse) =>
  `${format(parseISO(group.start), "d MMMM yyyy", {
    locale: sv,
  })}${group.arenaStart ? ` ${group.arenaStart}` : ""}`;
